<script lang="ts" setup>
import * as _ from 'lodash-es'
import * as dateFns from 'date-fns'
import Filter from '../Filter.vue'
import FloatLabel from 'primevue/floatlabel'
import MultiSelect from 'primevue/multiselect'
import Calendar from 'primevue/calendar'
import Dropdown from 'primevue/dropdown'
import RadioButton from 'primevue/radiobutton'
import { formatList } from '@/lib/helpers'

const filterInputStore = useFilterInputStore()
const { getFirstNames, getFamilyNames, getPlaces } = useRemoteData()

const clonedFilterData = useClonedObject(filterInputStore.values)

function onCancel() {
  clonedFilterData.cancel()
}

function onCommit() {
  clonedFilterData.commit()
}

const options = [
  'firstName',
  'familyName',
  'gender',
  'placeOfOrigin',
  'placeOfResidence',
  'placeOfBirth',
  'placeOfDeath',
  'dateOfBirthRange',
  'dateOfDeathRange',
] as const

const isActive = computed(() => options.some(option => filterInputStore.isOptionActive[option]))

function onClear() {
  filterInputStore.reset({ include: options })
}
</script>

<template>
  <FilterGroup
    name="Personenangaben"
    :active="isActive"
    @cancel="onCancel"
    @commit="onCommit"
    @clear="onClear"
  >
    <template #icon>
      <!-- prettier-ignore -->
      <svg width="20" height="20" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M437 331a255 255 0 0 0-97.3-61 148 148 0 1 0-167.4 0A255 255 0 0 0 75 331 254.3 254.3 0 0 0 0 512h40c0-119.1 96.9-216 216-216s216 96.9 216 216h40a254 254 0 0 0-75-181Zm-181-75a108.1 108.1 0 0 1 0-216 108.1 108.1 0 0 1 0 216Z"/></svg>
    </template>
    <template #body>
      <FloatLabel>
        <RemoteAutoComplete
          v-model="clonedFilterData.data.firstName"
          :get-suggestions="getFirstNames"
          option-label="name"
          multiple
        />
        <label>Vorname</label>
      </FloatLabel>
      <FloatLabel>
        <RemoteAutoComplete
          v-model="clonedFilterData.data.familyName"
          :get-suggestions="getFamilyNames"
          option-label="name"
          multiple
        />
        <label>Familienname</label>
      </FloatLabel>
      <div>
        <p style="margin-top: 0">Geschlecht:</p>
        <label>
          <RadioButton v-model="clonedFilterData.data.gender" name="gender" value="all" />
          &nbsp;alle
        </label>
        <label>
          <RadioButton v-model="clonedFilterData.data.gender" name="gender" value="male" />
          &nbsp;männlich
        </label>
        <label>
          <RadioButton v-model="clonedFilterData.data.gender" name="gender" value="female" />
          &nbsp;weiblich
        </label>
        <label>
          <RadioButton v-model="clonedFilterData.data.gender" name="gender" value="others" />
          &nbsp;andere
        </label>
      </div>

      <FloatLabel>
        <RemoteAutoComplete
          v-model="clonedFilterData.data.placeOfOrigin"
          :get-suggestions="getPlaces"
          option-label="name"
          multiple
        />
        <label>Heimatort</label>
      </FloatLabel>
      <FloatLabel>
        <RemoteAutoComplete
          v-model="clonedFilterData.data.placeOfResidence"
          :get-suggestions="getPlaces"
          option-label="name"
          multiple
        />
        <label>Wohnort</label>
      </FloatLabel>

      <div>
        <label>Geburtsdatum</label>
        <DateRangeInput
          primary-aspect="date"
          :slider-steps="25"
          :min-date="filterInputStore.minDate"
          :max-date="filterInputStore.maxDate"
          :close-on-escape="false"
          v-model="clonedFilterData.data.dateOfBirthRange"
        />
      </div>
      <div>
        <label>Sterbedatum</label>
        <DateRangeInput
          primary-aspect="date"
          :slider-steps="25"
          :min-date="filterInputStore.minDate"
          :max-date="filterInputStore.maxDate"
          :close-on-escape="false"
          v-model="clonedFilterData.data.dateOfDeathRange"
        />
      </div>
      <FloatLabel>
        <RemoteAutoComplete
          v-model="clonedFilterData.data.placeOfBirth"
          :get-suggestions="getPlaces"
          option-label="name"
          multiple
        />
        <label>Geburtsort</label>
      </FloatLabel>
      <FloatLabel>
        <RemoteAutoComplete
          v-model="clonedFilterData.data.placeOfDeath"
          :get-suggestions="getPlaces"
          option-label="name"
          multiple
        />
        <label>Sterbeort</label>
      </FloatLabel>
    </template>
    <template #summary>
      <div v-if="filterInputStore.isOptionActive.firstName" class="summary-row">
        Vorname:&nbsp;
        <strong>{{ formatList(_.map(filterInputStore.values.firstName, 'name')) }}</strong>
      </div>
      <div v-if="filterInputStore.isOptionActive.familyName" class="summary-row">
        Familienname:&nbsp;
        <strong>{{ formatList(_.map(filterInputStore.values.familyName, 'name')) }}</strong>
      </div>
      <div v-if="filterInputStore.isOptionActive.gender">
        Geschlecht:
        <strong>{{
          { all: 'alle', male: 'männlich', female: 'weiblich', others: 'divers' }[
            filterInputStore.values.gender
          ]
        }}</strong>
      </div>
      <div v-if="filterInputStore.isOptionActive.placeOfOrigin" class="summary-row">
        Heimatort:&nbsp;
        <strong>{{ formatList(_.map(filterInputStore.values.placeOfOrigin, 'name')) }}</strong>
      </div>
      <div v-if="filterInputStore.isOptionActive.placeOfResidence" class="summary-row">
        Wohnort:&nbsp;
        <strong>{{ formatList(_.map(filterInputStore.values.placeOfResidence, 'name')) }}</strong>
      </div>
      <div v-if="filterInputStore.isOptionActive.dateOfBirthRange" class="summary-row">
        Geburtsdatum:
        <strong>
          <template
            v-if="
              !filterInputStore.isMinDate(filterInputStore.values.dateOfBirthRange?.[0]) &&
              !filterInputStore.isMaxDate(filterInputStore.values.dateOfBirthRange?.[1])
            "
          >
            zwischen
            {{ dateFns.format(filterInputStore.values.dateOfBirthRange![0], 'dd.MM.yyyy') }}
            und
            {{ dateFns.format(filterInputStore.values.dateOfBirthRange![1], 'dd.MM.yyyy') }}
          </template>
          <template
            v-else-if="!filterInputStore.isMinDate(filterInputStore.values.dateOfBirthRange?.[0])"
          >
            ab {{ dateFns.format(filterInputStore.values.dateOfBirthRange![0], 'dd.MM.yyyy') }}
          </template>
          <template
            v-else="!filterInputStore.isMinDate(filterInputStore.values.dateOfBirthRange?.[1])"
          >
            bis {{ dateFns.format(filterInputStore.values.dateOfBirthRange![1], 'dd.MM.yyyy') }}
          </template>
        </strong>
      </div>
      <div v-if="filterInputStore.isOptionActive.dateOfDeathRange" class="summary-row">
        Sterbedatum:
        <strong>
          <template
            v-if="
              !filterInputStore.isMinDate(filterInputStore.values.dateOfDeathRange?.[0]) &&
              !filterInputStore.isMaxDate(filterInputStore.values.dateOfDeathRange?.[1])
            "
          >
            zwischen
            {{ dateFns.format(filterInputStore.values.dateOfDeathRange![0], 'dd.MM.yyyy') }}
            und
            {{ dateFns.format(filterInputStore.values.dateOfDeathRange![1], 'dd.MM.yyyy') }}
          </template>
          <template
            v-else-if="!filterInputStore.isMinDate(filterInputStore.values.dateOfDeathRange?.[0])"
          >
            ab {{ dateFns.format(filterInputStore.values.dateOfDeathRange![0], 'dd.MM.yyyy') }}
          </template>
          <template
            v-else="!filterInputStore.isMinDate(filterInputStore.values.dateOfDeathRange?.[1])"
          >
            bis {{ dateFns.format(filterInputStore.values.dateOfDeathRange![1], 'dd.MM.yyyy') }}
          </template>
        </strong>
      </div>
      <div v-if="filterInputStore.isOptionActive.placeOfBirth" class="summary-row">
        Geburtsort:
        <strong>{{ formatList(_.map(filterInputStore.values.placeOfBirth, 'name')) }}</strong>
      </div>
      <div v-if="filterInputStore.isOptionActive.placeOfDeath" class="summary-row">
        Sterbeort:
        <strong>{{ formatList(_.map(filterInputStore.values.placeOfDeath, 'name')) }}</strong>
      </div>
    </template>
  </FilterGroup>
</template>

<style scoped lang="scss">
.summary-row {
  // display: flex;
  width: 100%;
  text-wrap: balance;

  &:not(:last-child) {
    margin-bottom: 0.5rem;
  }
}
</style>
